// Pure-TypeScript stand-in for the Wasm engine. Used when the engine cannot
// load (and by the temporary engine stub during development). It produces the
// same shapes as the engine contract with simpler algorithms: patch- or
// Myers-based blocks, prefix/suffix intraline emphasis, no highlighting,
// symbols or move detection (blocks still report their move runs), and the
// engine's attention rules ported directly.
import type {
  AttentionInput,
  AttentionOverride,
  AttentionMember,
  AttentionResult,
  Block,
  BlockSet,
  BlocksInput,
  CodeSymbol,
  EngineApi,
  Emphasis,
  Highlight,
  LanguageInfo,
  LegacyResolution,
  Move,
  MoveInput,
  MoveRun,
  PatchHunk,
  Reason,
  Row,
  Side,
} from '../engine/types';
// Unlike the engine's eq_ignore_ascii_case, sameLogin finds an empty login
// equal to nothing; unreachable, as timeline.ts substitutes 'ghost' for it.
import { loginKey, sameLogin } from '../logic/attention';
import { parsePatch } from '../logic/patch';
import { compareTimes } from '../logic/time';
import { FALLBACK_PREFIX } from './keys';

const LANGS: LanguageInfo[] = [
  { id: 'rust', name: 'Rust', extensions: ['rs'] },
  { id: 'typescript', name: 'TypeScript', extensions: ['ts', 'mts', 'cts'] },
  { id: 'tsx', name: 'TSX', extensions: ['tsx'] },
  { id: 'javascript', name: 'JavaScript', extensions: ['js', 'mjs', 'cjs', 'jsx'] },
  { id: 'python', name: 'Python', extensions: ['py'] },
  { id: 'go', name: 'Go', extensions: ['go'] },
  { id: 'json', name: 'JSON', extensions: ['json'] },
  { id: 'toml', name: 'TOML', extensions: ['toml'] },
  { id: 'yaml', name: 'YAML', extensions: ['yml', 'yaml'] },
  { id: 'bash', name: 'Bash', extensions: ['sh', 'bash'] },
  { id: 'css', name: 'CSS', extensions: ['css'] },
  { id: 'html', name: 'HTML', extensions: ['html', 'htm'] },
  { id: 'c', name: 'C', extensions: ['c', 'h'] },
  { id: 'cpp', name: 'C++', extensions: ['cc', 'cpp', 'cxx', 'hpp', 'hh'] },
  { id: 'java', name: 'Java', extensions: ['java'] },
  { id: 'ruby', name: 'Ruby', extensions: ['rb'] },
  { id: 'markdown', name: 'Markdown', extensions: ['md', 'markdown'] },
];

export function detectLanguage(path: string): string | null {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  return LANGS.find((l) => l.extensions.includes(ext))?.id ?? null;
}

function lines(text: string): string[] {
  if (text === '') return [];
  return (text.endsWith('\n') ? text.slice(0, -1) : text).split('\n');
}

/**
 * Stable block key for the fallback: FALLBACK_PREFIX + 64-bit FNV-1a hex.
 * Deliberately distinct from the engine's keys (see diff/keys.ts), so marks
 * are disabled rather than stored under keys the engine would not produce.
 */
export function hashKey(parts: string[]): string {
  return FALLBACK_PREFIX + fnv1a(parts);
}

/**
 * hashKey made unique within one file (the engine's `unique`): the n-th
 * repeat of the same parts in `seen` has `String(n)` appended as another
 * part, and the first occurrence keeps the plain key.
 */
function uniqueKey(seen: Map<string, number>, parts: string[]): string {
  const base = hashKey(parts);
  const n = seen.get(base) ?? 0;
  seen.set(base, n + 1);
  return n === 0 ? base : hashKey([...parts, String(n)]);
}

/** 64-bit FNV-1a of the parts, as 16 hex digits. Each part is preceded by
 *  its UTF-8 byte length (8 bytes, little-endian), so no part can run into
 *  the next whatever it contains. */
export function fnv1a(parts: string[]): string {
  let h = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const enc = new TextEncoder();
  const feed = (b: number) => {
    h ^= BigInt(b);
    h = (h * prime) & 0xffffffffffffffffn;
  };
  for (const part of parts) {
    const bytes = enc.encode(part);
    let n = bytes.length;
    for (let i = 0; i < 8; i++, n = Math.floor(n / 256)) feed(n % 256);
    for (const b of bytes) feed(b);
  }
  return h.toString(16).padStart(16, '0');
}

type Op = { kind: 'context' | 'add' | 'del'; old: number | null; new: number | null };

/**
 * Edit distance past which the fallback's diff gives up on aligning the
 * region between the common prefix and suffix and shows it as a full
 * replacement. Keeps time at O((N+M)·D) and the trace at O(D²) memory
 * (16 MB at this cap) on the main thread.
 */
export const MAX_EDIT_DISTANCE = 2000;

/** Myers O(ND) line diff. Returns ops with 1-based line numbers. The common
 *  prefix and suffix are trimmed first; past MAX_EDIT_DISTANCE the rest is
 *  a full replacement. */
export function myers(a: string[], b: string[], norm: (s: string) => string = (s) => s): Op[] {
  const A = a.map(norm);
  const B = b.map(norm);
  let pre = 0;
  while (pre < A.length && pre < B.length && A[pre] === B[pre]) pre++;
  let suf = 0;
  while (suf < A.length - pre && suf < B.length - pre && A[A.length - 1 - suf] === B[B.length - 1 - suf]) suf++;
  const ops: Op[] = [];
  for (let i = 1; i <= pre; i++) ops.push({ kind: 'context', old: i, new: i });
  for (const o of myersMiddle(A.slice(pre, A.length - suf), B.slice(pre, B.length - suf)))
    ops.push({ kind: o.kind, old: o.old === null ? null : o.old + pre, new: o.new === null ? null : o.new + pre });
  for (let i = suf; i > 0; i--) ops.push({ kind: 'context', old: A.length - i + 1, new: B.length - i + 1 });
  return ops;
}

/** Myers on already-normalized lines, keeping only the k-window -d-1…d+1
 *  of V per step for the backtrack. */
function myersMiddle(A: string[], B: string[]): Op[] {
  const n = A.length;
  const m = B.length;
  const max = Math.min(n + m, MAX_EDIT_DISTANCE);
  const off = max + 1;
  const v = new Int32Array(2 * max + 3);
  const trace: Int32Array[] = [];
  let done = n === 0 && m === 0;
  for (let d = 0; d <= max && !done; d++) {
    trace.push(v.slice(off - d - 1, off + d + 2));
    for (let k = -d; k <= d; k += 2) {
      let x = k === -d || (k !== d && v[off + k - 1] < v[off + k + 1]) ? v[off + k + 1] : v[off + k - 1] + 1;
      let y = x - k;
      while (x < n && y < m && A[x] === B[y]) {
        x++;
        y++;
      }
      v[off + k] = x;
      if (x >= n && y >= m) {
        done = true;
        break;
      }
    }
  }
  const ops: Op[] = [];
  if (!done) {
    // Too different to align within the cap: a full replacement.
    for (let i = 1; i <= n; i++) ops.push({ kind: 'del', old: i, new: null });
    for (let j = 1; j <= m; j++) ops.push({ kind: 'add', old: null, new: j });
    return ops;
  }
  let x = n;
  let y = m;
  for (let d = trace.length - 1; d >= 0 && (x > 0 || y > 0); d--) {
    const w = trace[d];
    const at = (k: number) => w[k + d + 1];
    const k = x - y;
    const prevK = k === -d || (k !== d && at(k - 1) < at(k + 1)) ? k + 1 : k - 1;
    const prevX = at(prevK);
    const prevY = prevX - prevK;
    while (x > prevX && y > prevY) {
      ops.push({ kind: 'context', old: x, new: y });
      x--;
      y--;
    }
    if (d > 0) {
      if (x === prevX) ops.push({ kind: 'add', old: null, new: y });
      else ops.push({ kind: 'del', old: x, new: null });
    }
    x = prevX;
    y = prevY;
  }
  while (x > 0 && y > 0) {
    ops.push({ kind: 'context', old: x, new: y });
    x--;
    y--;
  }
  return ops.reverse();
}

function hunksFromOps(ops: Op[], context: number): PatchHunk[] {
  const hunks: PatchHunk[] = [];
  const changes = ops.map((o, i) => (o.kind !== 'context' ? i : -1)).filter((i) => i >= 0);
  if (!changes.length) return hunks;
  let start = Math.max(0, changes[0] - context);
  let end = Math.min(ops.length, changes[0] + context + 1);
  const flush = () => {
    const rows = ops.slice(start, end).map((o) => ({ ...o, text: '', noNewline: false }));
    // Lines before the hunk on each side.
    const before = ops.slice(0, start);
    const oldFrom = before.filter((o) => o.old !== null).length;
    const newFrom = before.filter((o) => o.new !== null).length;
    const oldLines = rows.filter((r) => r.old !== null).length;
    const newLines = rows.filter((r) => r.new !== null).length;
    hunks.push({
      // Unified convention: an empty side's start is the line before.
      oldStart: oldLines === 0 ? oldFrom : oldFrom + 1,
      oldLines,
      newStart: newLines === 0 ? newFrom : newFrom + 1,
      newLines,
      header: '',
      rows,
    });
  };
  for (const c of changes.slice(1)) {
    if (c - context <= end) end = Math.min(ops.length, c + context + 1);
    else {
      flush();
      start = Math.max(0, c - context);
      end = Math.min(ops.length, c + context + 1);
    }
  }
  flush();
  return hunks;
}

function emphasisPair(a: string, b: string): [Emphasis[], Emphasis[]] {
  let s = 0;
  while (s < a.length && s < b.length && a[s] === b[s]) s++;
  let e = 0;
  while (e < a.length - s && e < b.length - s && a[a.length - 1 - e] === b[b.length - 1 - e]) e++;
  if (s === 0 && e === 0) return [[], []];
  const ea = s < a.length - e ? [{ start: s, end: a.length - e }] : [];
  const eb = s < b.length - e ? [{ start: s, end: b.length - e }] : [];
  return [ea, eb];
}

/**
 * A line as compared when ignoring whitespace: every whitespace character
 * removed (crates/engine/src/text.rs strip_whitespace, `git diff -w`). JS
 * `\s` and Rust's `char::is_whitespace` differ only on U+FEFF (whitespace
 * here) and U+0085 (whitespace there).
 */
const normWs = (s: string) => s.replace(/\s+/g, '');

const stripCr = (s: string) => (s.endsWith('\r') ? s.slice(0, -1) : s);

/**
 * Whether the patch's rows on `side` agree with that side's lines (the
 * engine's check): every row names an existing line, in increasing order,
 * with the same text (ignoring a trailing CR), and every hunk lies within
 * the text.
 */
export function sideMatches(hunks: PatchHunk[], text: string[], side: 'old' | 'new'): boolean {
  let prev = 0;
  for (const h of hunks) {
    const [start, count] = side === 'old' ? [h.oldStart, h.oldLines] : [h.newStart, h.newLines];
    if ((count === 0 ? start : start + count - 1) > text.length) return false;
    for (const r of h.rows) {
      const line = r[side];
      if (line === null) continue;
      if (line <= prev || line > text.length || stripCr(text[line - 1]) !== stripCr(r.text)) return false;
      prev = line;
    }
  }
  return true;
}

/**
 * Whether every line outside the patch's hunks is the same on both sides
 * (the engine's check): with sideMatches on both sides, the patch shows
 * every difference. An empty, binary or partial patch fails it.
 */
export function gapsEqual(hunks: PatchHunk[], oldText: string[], newText: string[]): boolean {
  let o = 0;
  let n = 0;
  const gap = (oldEnd: number, newEnd: number) => {
    if (oldEnd < o || newEnd < n || oldEnd - o !== newEnd - n || oldEnd > oldText.length || newEnd > newText.length)
      return false;
    for (let i = 0; i < oldEnd - o; i++) if (oldText[o + i] !== newText[n + i]) return false;
    return true;
  };
  for (const h of hunks) {
    // Unified convention: an empty side's start is the line before.
    const ob = h.oldLines === 0 ? h.oldStart : Math.max(0, h.oldStart - 1);
    const nb = h.newLines === 0 ? h.newStart : Math.max(0, h.newStart - 1);
    if (!gap(ob, nb)) return false;
    o = ob + h.oldLines;
    n = nb + h.newLines;
  }
  return gap(oldText.length, newText.length);
}

/** Whether the hunk's rows fill its header's counts on both sides. */
function isComplete(h: PatchHunk): boolean {
  return h.rows.filter((r) => r.old !== null).length === h.oldLines && h.rows.filter((r) => r.new !== null).length === h.newLines;
}

/** A side's final-newline state from the patch: the `noNewline` of the row
 *  showing the side's last line, or null when no hunk reaches it. */
function eolFromPatch(hunks: PatchHunk[], count: number, side: Side): boolean | null {
  if (count === 0) return null;
  for (const h of hunks) for (const r of h.rows) if (r[side] === count) return r.noNewline;
  return null;
}

export function blocks(input: BlocksInput): BlockSet {
  const oldLines = lines(input.oldText);
  const newLines = lines(input.newText);
  const patchHunks = input.patch ? parsePatch(input.patch) : [];
  const oldMatches = !!input.patch && sideMatches(patchHunks, oldLines, 'old');
  const newMatches = !!input.patch && sideMatches(patchHunks, newLines, 'new');
  const norm = input.options.ignoreWhitespace ? normWs : (s: string) => s;
  // The patch describes the texts: every hunk is complete, both sides
  // match, and it shows every line that differs.
  const describes =
    !!input.patch && patchHunks.every(isComplete) && oldMatches && newMatches && gapsEqual(patchHunks, oldLines, newLines);

  // Commentable lines: inside the patch hunks, per side, on a side whose
  // text is the patch's (so not the old side of an interdiff from a
  // snapshot).
  const okOld = new Set<number>();
  const okNew = new Set<number>();
  for (const h of patchHunks)
    for (const r of h.rows) {
      if (r.old !== null && oldMatches) okOld.add(r.old);
      if (r.new !== null && newMatches) okNew.add(r.new);
    }

  // Final newline: the patch's markers where it describes the texts and a
  // hunk reaches that side's last line, else the text.
  const noEol = (text: string) => text !== '' && !text.endsWith('\n');
  const oldEol = describes ? eolFromPatch(patchHunks, oldLines.length, 'old') : null;
  const newEol = describes ? eolFromPatch(patchHunks, newLines.length, 'new') : null;
  const oldNoEol = oldEol ?? noEol(input.oldText);
  const newNoEol = newEol ?? noEol(input.newText);
  const eolChanged = !input.options.ignoreWhitespace && oldNoEol !== newNoEol;
  // Lines as compared (the engine's `compared`): a last line that lacks its
  // newline, when the other side's has one, is a different line. Rows and
  // emphasis read the plain lines.
  const marked = (ls: string[], noEol: boolean) =>
    eolChanged && noEol && ls.length ? [...ls.slice(0, -1), ls[ls.length - 1] + '\n\\ No newline at end of file'] : ls;
  const oldCmp = marked(oldLines, oldNoEol);
  const newCmp = marked(newLines, newNoEol);

  // A final-newline change the patch's hunks do not reach is missing from them.
  const showsEol =
    !eolChanged || ((oldLines.length === 0 || oldEol !== null) && (newLines.length === 0 || newEol !== null));
  // Without the engine there is one alignment, whatever `options.algorithm`
  // asks (the Files view says so): the patch's hunks where they describe
  // the texts (GitHub's, as Myers aligns them), else its own Myers diff,
  // whose hunks take the header of the patch hunk holding their first
  // change where the patch describes the texts.
  const usePatch = describes && showsEol && !input.recompute && !input.options.ignoreWhitespace;
  const hunks = usePatch ? patchHunks : hunksFromOps(myers(oldCmp, newCmp, norm), input.options.context);
  if (describes && !usePatch)
    for (const h of hunks) {
      const first = h.rows.find((r) => r.kind !== 'context');
      const holds = (p: PatchHunk) =>
        first?.kind === 'add'
          ? first.new !== null && first.new >= p.newStart && first.new < p.newStart + p.newLines
          : !!first && first.old !== null && first.old >= p.oldStart && first.old < p.oldStart + p.oldLines;
      h.header = patchHunks.find(holds)?.header ?? '';
    }

  const out: Block[] = [];
  const seen = new Map<string, number>();
  const anchors = runAnchors(hunks, newLines, (j) => norm(newCmp[j]));
  const runs: PendingRun[] = [];
  let o = 1;
  let n = 1;
  const ctx = (toOld: number, toNew: number) => {
    const rows: Row[] = [];
    while (o < toOld || n < toNew) {
      const ro = o < toOld ? o++ : null;
      const rn = n < toNew ? n++ : null;
      rows.push({
        kind: 'context',
        old: ro,
        new: rn,
        emphasis: [],
        pair: null,
        commentableOld: ro !== null && okOld.has(ro),
        commentableNew: rn !== null && okNew.has(rn),
        moveId: null,
        hash: null,
        run: null,
        alt: [],
      });
    }
    if (rows.length)
      out.push({
        key: hashKey([input.path, 'ctx', String(rows[0].old ?? 0), String(rows[0].new ?? 0)]),
        kind: 'context',
        title: `${rows.length} unchanged line${rows.length === 1 ? '' : 's'}`,
        added: 0,
        removed: 0,
        rows,
        changeKeys: [],
      });
  };
  let added = 0;
  let removed = 0;
  for (const h of hunks) {
    // An empty side's start is the line before the hunk.
    const firstOld = h.rows.find((r) => r.old !== null)?.old ?? h.oldStart + 1;
    const firstNew = h.rows.find((r) => r.new !== null)?.new ?? h.newStart + 1;
    ctx(firstOld, firstNew);
    const rows: Row[] = h.rows.map((r) => ({
      kind: r.kind,
      old: r.old,
      new: r.new,
      emphasis: [],
      pair: null,
      commentableOld: r.old !== null && okOld.has(r.old) && r.kind !== 'add',
      commentableNew: r.new !== null && okNew.has(r.new) && r.kind !== 'del',
      moveId: null,
      hash: null,
      run: null,
      alt: [],
    }));
    // Pair runs of deletions with the following additions.
    for (let i = 0; i < rows.length; ) {
      if (rows[i].kind === 'context') {
        i++;
        continue;
      }
      const dels: number[] = [];
      const adds: number[] = [];
      while (i < rows.length && rows[i].kind !== 'context') (rows[i].kind === 'del' ? dels : adds).push(i++);
      for (let j = 0; j < Math.min(dels.length, adds.length); j++) {
        const d = rows[dels[j]];
        const a = rows[adds[j]];
        d.pair = adds[j];
        a.pair = dels[j];
        const [ed, ea] = emphasisPair(oldLines[d.old! - 1] ?? '', newLines[a.new! - 1] ?? '');
        d.emphasis = ed;
        a.emphasis = ea;
      }
    }
    // Each maximal run of del/add rows is keyed once the whole file is
    // known (`keyRuns`); until then a row's `run` counts the block's runs.
    // Each changed row gets its content hash (the engine's `Row.hash`).
    const changeKeys: string[] = [];
    const block = out.length;
    let ordinal = 0;
    const cmp = (r: Row) => norm(r.kind === 'del' ? (oldCmp[r.old! - 1] ?? '') : (newCmp[r.new! - 1] ?? ''));
    // New lines before the current row (0-based index of the next one).
    let cursor = firstNew - 1;
    for (let i = 0; i < rows.length; ) {
      if (rows[i].kind === 'context') {
        if (rows[i].new !== null) cursor = rows[i].new!;
        i++;
        continue;
      }
      const run: Row[] = [];
      for (; i < rows.length && rows[i].kind !== 'context'; i++) run.push(rows[i]);
      const del = run.filter((r) => r.kind === 'del').map(cmp);
      const add = run.filter((r) => r.kind === 'add').map(cmp);
      const above = anchors.above(cursor);
      cursor += add.length;
      const below = anchors.below(cursor);
      const key = hashKey([input.path, 'run', String(del.length), ...del, String(add.length), ...add, anchors.part(above), anchors.part(below)]);
      runs.push({ block, key, start: cursor - add.length, added: add, above, below });
      for (const r of run) {
        r.hash = rowHash(input.path, r.kind === 'del' ? 'old' : 'new', cmp(r));
        r.run = ordinal;
      }
      ordinal++;
    }
    const removedText = rows.filter((r) => r.kind === 'del').map((r) => norm(oldLines[r.old! - 1] ?? ''));
    const addedText = rows.filter((r) => r.kind === 'add').map((r) => norm(newLines[r.new! - 1] ?? ''));
    const a = addedText.length;
    const d = removedText.length;
    added += a;
    removed += d;
    out.push({
      key: uniqueKey(seen, [input.path, ...removedText, '\u0001', ...addedText]),
      kind: 'change',
      title: h.header || 'Changed region',
      added: a,
      removed: d,
      rows,
      changeKeys,
    });
    o = Math.max(o, firstOld + h.rows.filter((r) => r.old !== null).length);
    n = Math.max(n, firstNew + h.rows.filter((r) => r.new !== null).length);
  }
  ctx(oldLines.length + 1, newLines.length + 1);
  keyRuns(out, runs, anchors, newLines.map((_, j) => norm(newCmp[j])));
  // The texts differ, but line for line only in whitespace.
  const whitespaceOnly =
    input.options.ignoreWhitespace &&
    input.oldText !== input.newText &&
    oldLines.length === newLines.length &&
    oldLines.every((l, i) => normWs(l) === normWs(newLines[i]));
  return {
    blocks: out,
    oldLineCount: oldLines.length,
    newLineCount: newLines.length,
    added,
    removed,
    whitespaceOnly,
    oldNoNewlineAtEof: oldNoEol,
    newNoNewlineAtEof: newNoEol,
    moveRuns: moveRuns(out, oldLines, newLines),
    simpleDiff: null,
  };
}

/** The most anchors a change key reads on each side of its run (the engine's `MAX_ANCHORS`). */
const MAX_ANCHORS = 64;

/** A change run waiting for its key (the engine's `PendingRun`): its
 *  block, the key from its lines and nearest anchors, where its added
 *  lines start (new line index), those lines as compared, and its nearest
 *  anchors (new line indexes). */
interface PendingRun {
  block: number;
  key: string;
  start: number;
  added: string[];
  above: number | null;
  below: number | null;
}

/** The shape of a class told apart by order (the engine's `shape`): how
 *  many, and the distances between consecutive ones. */
function shape(at: number[]): string {
  return [String(at.length), ...at.slice(1).map((a, i) => String(a - at[i]))].join(',');
}

/** The key of the `n`-th of a class with shape `s` (the engine's
 *  `ordinal_key`): a twin appearing or going, or the spacing changing,
 *  gives every member a new key, so a mark clears rather than passing to
 *  another twin. */
function ordinalKey(key: string, n: number, s: string): string {
  return hashKey([key, 'run#', String(n), s]);
}

/** Where a run's added lines could be in the new text (the engine's `Place`). */
interface Place {
  at: number;
  above: number | null;
  below: number | null;
}

/**
 * Keys every change run of the file (the engine's `key_runs`, the same
 * steps; the bytes hashed differ, so engine and fallback keys never match).
 * Twins are judged against the new text, so a change has one key in every
 * comparison that ends at it: a run's places are where its added lines
 * appear between anchors with the same lines as its nearest ones (for a run
 * that adds nothing, the gaps between consecutive anchors with those
 * lines). A run with one place keeps the key from its lines and nearest
 * anchors; otherwise it reads one more anchor on each side at a time until
 * no other place reads the same; places alike when both sides run out or
 * MAX_ANCHORS were read get their order in the file and the shape of their
 * class (`ordinalKey`). Runs of this comparison that still share a key
 * (deletions between the same two anchors) get their own order and shape,
 * so no two runs of the file share a key, and a twin appearing or going
 * clears these keys rather than passing them to another twin.
 * Fills in each block's `changeKeys`, one per run in order, which its
 * rows' `run` (the run's number in the block) indexes.
 */
function keyRuns(blocks: Block[], runs: PendingRun[], anchors: ReturnType<typeof runAnchors>, lines: string[]) {
  if (!runs.length) return;
  const interned = new Map<string, number>();
  const ids = lines.map((l) => {
    let n = interned.get(l);
    if (n === undefined) interned.set(l, (n = interned.size));
    return n;
  });
  const id = (j: number | null) => (j === null ? -1 : ids[j]);
  // New line indexes by their line and nearest anchor above; gaps between
  // consecutive anchors by the anchors' lines.
  const starts = new Map<string, number[]>();
  lines.forEach((l, j) => {
    const k = `${ids[j]}:${id(anchors.above(j))}`;
    const list = starts.get(k);
    if (list) list.push(j);
    else starts.set(k, [j]);
  });
  const gaps = new Map<string, [number | null, number | null][]>();
  for (let prev: number | null = null, j = 0; ; ) {
    const next = anchors.below(j);
    const k = `${id(prev)}:${id(next)}`;
    const list = gaps.get(k);
    if (list) list.push([prev, next]);
    else gaps.set(k, [[prev, next]]);
    if (next === null) break;
    prev = next;
    j = next + 1;
  }
  const like = (r: PendingRun): Place[] => {
    if (!r.added.length)
      return (gaps.get(`${id(r.above)}:${id(r.below)}`) ?? []).map(([a, b]) => ({ at: a === null ? 0 : a + 1, above: a, below: b }));
    const out: Place[] = [];
    for (const at of starts.get(`${interned.get(r.added[0]) ?? -2}:${id(r.above)}`) ?? []) {
      const end = at + r.added.length;
      if (end > lines.length || r.added.some((l, i) => lines[at + i] !== l)) continue;
      const below = anchors.below(end);
      if (id(below) === id(r.below)) out.push({ at, above: anchors.above(at), below });
    }
    return out;
  };
  const widen = (places: Place[]) => {
    const out = new Map<number, [number, [number, string] | null]>();
    let classes = [places.map((p) => ({ ...p }))];
    for (let steps = 0; classes.length; steps++) {
      const next: Place[][] = [];
      for (const cls of classes) {
        if (cls.length === 1) out.set(cls[0].at, [steps, null]);
        else if (steps + 1 >= MAX_ANCHORS || (cls[0].above === null && cls[0].below === null)) {
          const s = shape(cls.map((p) => p.at));
          cls.forEach((p, n) => out.set(p.at, [steps, [n, s]]));
        }
        else {
          const parts = new Map<string, Place[]>();
          for (const p of cls) {
            p.above = p.above === null ? null : anchors.above(p.above);
            p.below = p.below === null ? null : anchors.below(p.below + 1);
            const k = `${id(p.above)}:${id(p.below)}`;
            const part = parts.get(k);
            if (part) part.push(p);
            else parts.set(k, [p]);
          }
          next.push(...parts.values());
        }
      }
      classes = next;
    }
    return out;
  };
  const found = new Map<string, Map<number, [number, [number, string] | null]>>();
  const keys = runs.map((r) => {
    const own: Place = { at: r.added.length ? r.start : r.above === null ? 0 : r.above + 1, above: r.above, below: r.below };
    const group = `${r.added.length}\u0001${r.added.join('\u0001')}\u0001${id(r.above)}:${id(r.below)}`;
    let widened = found.get(group);
    if (!widened?.has(own.at)) {
      const alike = like(r);
      if (!alike.some((p) => p.at === own.at)) alike.push(own), alike.sort((a, b) => a.at - b.at);
      widened = widen(alike);
      found.set(group, widened);
    }
    const [steps, ordinal] = widened.get(own.at)!;
    let key = r.key;
    let { above, below } = r;
    for (let s = 0; s < steps; s++) {
      above = above === null ? null : anchors.above(above);
      below = below === null ? null : anchors.below(below + 1);
      key = hashKey([key, 'run+', anchors.part(above), anchors.part(below)]);
    }
    return ordinal === null ? key : ordinalKey(key, ...ordinal);
  });
  // Runs of this comparison at one place (identical deletions in one gap):
  // told apart by their order and their shape.
  const atOnePlace = new Map<string, number[]>();
  keys.forEach((k, i) => {
    const g = atOnePlace.get(k);
    if (g) g.push(i);
    else atOnePlace.set(k, [i]);
  });
  for (const group of atOnePlace.values()) {
    if (group.length < 2) continue;
    const s = shape(group.map((i) => runs[i].start));
    group.forEach((i, n) => (keys[i] = ordinalKey(keys[i], n, s)));
  }
  runs.forEach((r, i) => blocks[r.block].changeKeys.push(keys[i]));
}

/**
 * Change-run anchors (the engine's `Anchors`): the nearest new-side line
 * that no hunk adds and that is not blank, before new line index `k`
 * (0-based) or at or after it; `part` makes one a key part: '0' for none,
 * else '1' and the line as compared.
 */
function runAnchors(hunks: PatchHunk[], newLines: string[], cmp: (j: number) => string) {
  const added = new Array<boolean>(newLines.length).fill(false);
  for (const h of hunks)
    for (const r of h.rows) if (r.kind === 'add' && r.new !== null && r.new >= 1 && r.new <= newLines.length) added[r.new - 1] = true;
  const anchor = (j: number) => !added[j] && newLines[j].trim() !== '';
  const above: (number | null)[] = [null];
  for (let k = 1; k <= newLines.length; k++) above.push(anchor(k - 1) ? k - 1 : above[k - 1]);
  const below: (number | null)[] = new Array(newLines.length + 1).fill(null);
  for (let k = newLines.length - 1; k >= 0; k--) below[k] = anchor(k) ? k : below[k + 1];
  const part = (j: number | null | undefined) => (j === null || j === undefined ? '0' : '1' + cmp(j));
  return { above: (k: number) => above[k] ?? null, below: (k: number) => below[k] ?? null, part };
}

/** A changed row's content hash (the engine's `Row.hash`): path, side and
 *  line as compared, nothing else. */
function rowHash(path: string, side: Side, line: string): string {
  return fnv1a([path, 'row', side, line]);
}

/**
 * The change runs of `blocks` (the engine's `move_runs`): per change block,
 * the maximal runs of removed (then added) rows with consecutive line
 * numbers, with the lines' text. Rows past the end of a text are skipped.
 */
export function moveRuns(blocks: Block[], oldLines: string[], newLines: string[]): MoveRun[] {
  const out: MoveRun[] = [];
  const sides = [
    ['old', 'del', oldLines],
    ['new', 'add', newLines],
  ] as const;
  for (const [side, kind, text] of sides)
    for (const block of blocks) {
      if (block.kind !== 'change') continue;
      let run: MoveRun | null = null;
      for (const row of block.rows) {
        const line = row.kind === kind ? row[side] : null;
        if (line === null || line < 1 || line > text.length || (run && run.startLine + run.lines.length !== line)) {
          if (run) out.push(run);
          run = null;
        }
        if (line === null || line < 1 || line > text.length) continue;
        run ??= { blockKey: block.key, side, startLine: line, lines: [] };
        run.lines.push(text[line - 1]);
      }
      if (run) out.push(run);
    }
  return out;
}

export function highlight(_language: string, text: string): Highlight {
  return { lines: lines(text).map(() => []), scopes: [] };
}

export function symbols(_language: string, _text: string): CodeSymbol[] {
  return [];
}

export function detectMoves(_files: MoveInput[]): Move[] {
  return [];
}

/** The engine's attention rules (crates/engine/src/attention.rs), ported
 *  directly; checked against the engine's case table in fallback.test.ts. */
export function attention(input: AttentionInput): AttentionResult {
  const members: AttentionMember[] = [];
  const find = (login: string) => members.find((m) => sameLogin(m.login, login));
  /** Adds `login`, whose turn begins `since`, or updates an existing member's reason (their turn goes on). */
  const truncated = !!input.truncated;
  /** Logins (keys) an event in the window certainly removed: a later join is exact. */
  const absent = new Set<string>();
  /** `exact`: `since` holds whatever the window shows (a manual add's write time, a join at a restart). */
  const add = (login: string, reason: Reason, since: string | null | undefined, exact = false) => {
    const m = find(login);
    if (m) m.reason = reason;
    else if (!since) members.push({ login, reason });
    else if (truncated && !exact && !absent.has(loginKey(login))) members.push({ login, reason, since, sinceBound: true });
    else members.push({ login, reason, since });
  };
  const remove = (login: string) => {
    for (let i = members.length - 1; i >= 0; i--) if (sameLogin(members[i].login, login)) members.splice(i, 1);
  };
  const removeCertainly = (login: string) => {
    absent.add(loginKey(login));
    remove(login);
  };
  /** Every member's turn starts again at `at`, exactly. */
  const restart = (at: string) => {
    for (const m of members) {
      m.since = at;
      delete m.sinceBound;
    }
  };
  const author = input.author;
  const isBot = (login: string) => input.bots.some((b) => sameLogin(b, login));
  const isAuthor = (login: string) => sameLogin(login, author);

  if (input.state !== 'OPEN') return { members: [] };
  if (input.draft) {
    const converted = input.events.some((e) => e.type === 'converted_to_draft');
    if (!isBot(author)) add(author, { kind: 'draft' }, truncated && !converted ? null : draftSince());
  } else computed();
  applyOverrides();
  return { members };

  /** The latest `at` of the events `want` keeps (the last of equal instants), or null. */
  function latestOf(want: (e: AttentionInput['events'][number]) => boolean): string | null {
    let latest: string | null = null;
    for (const e of input.events) if (want(e) && (latest === null || compareTimes(e.at, latest) >= 0)) latest = e.at;
    return latest;
  }

  /** When a draft's author began to hold it: its latest conversion to draft, else its opening. */
  function draftSince(): string | null {
    return latestOf((e) => e.type === 'converted_to_draft') ?? latestOf((e) => e.type === 'opened');
  }

  /** The set the timeline and requested list give an open, ready PR. */
  function computed() {
    // Stable sort: ties keep input order.
    const events = input.events
      .map((e, i) => [e, i] as const)
      .sort(([a, i], [b, j]) => compareTimes(a.at, b.at) || i - j)
      .map(([e]) => e);
    const unresolvedByOthers = input.threads.some((t) => !t.resolved && !isAuthor(t.startedBy));
    const anyUnresolved = input.threads.some((t) => !t.resolved);
    const unresolvedStartedBy = (login: string) => input.threads.some((t) => !t.resolved && sameLogin(t.startedBy, login));

    type Reviewer = { login: string; requested: boolean; commented: boolean; lastReview: string | null };
    const reviewers = new Map<string, Reviewer>();
    const reviewer = (login: string) => {
      let r = reviewers.get(loginKey(login));
      if (!r) reviewers.set(loginKey(login), (r = { login, requested: false, commented: false, lastReview: null }));
      return r;
    };

    // When the set last became empty (the fallback author's turn began then).
    let emptySince: string | null = null;
    for (const e of events) {
      const actor = e.actor;
      if (isBot(actor)) continue;
      const at = e.at;
      switch (e.type) {
        case 'opened':
        case 'ready_for_review':
        case 'reopened':
          // Waiting starts again: a draft or closed period was not.
          if (e.type !== 'opened') restart(at);
          for (const r of reviewers.values()) if (r.requested) add(r.login, { kind: 'reviewRequested' }, at, e.type !== 'opened');
          break;
        case 'converted_to_draft':
        case 'closed':
        case 'merged':
          break;
        case 'review_requested':
          if (!isAuthor(e.reviewer) && !isBot(e.reviewer)) {
            reviewer(e.reviewer).requested = true;
            add(e.reviewer, { kind: 'reviewRequested' }, at);
          }
          break;
        case 'review_request_removed':
          reviewer(e.reviewer).requested = false;
          if (find(e.reviewer)?.reason.kind === 'reviewRequested') remove(e.reviewer);
          break;
        case 'review':
        case 'comment':
        case 'push':
          if (e.type === 'review' && !isAuthor(actor)) {
            const r = reviewer(actor);
            r.requested = false;
            r.lastReview = e.state === 'DISMISSED' ? null : e.state;
            if (e.state === 'CHANGES_REQUESTED' || e.state === 'COMMENTED') r.commented = true;
            removeCertainly(actor);
            if (e.state === 'CHANGES_REQUESTED') add(author, { kind: 'changesRequestedBy', login: actor }, at);
            else if (e.state === 'APPROVED' && !anyUnresolved) add(author, { kind: 'readyToMerge' }, at);
            else if ((e.state === 'APPROVED' || e.state === 'COMMENTED') && unresolvedStartedBy(actor))
              add(author, { kind: 'unresolvedFrom', login: actor }, at);
          } else if (e.type === 'comment' && !isAuthor(actor)) {
            const r = reviewer(actor);
            r.commented = true;
            if (!r.requested) remove(actor);
            add(author, { kind: 'newCommentsFrom', login: actor }, at);
          } else if (e.type === 'push' && e.actorIsPusher && !isAuthor(actor)) {
            const requested = [...reviewers.values()].some((r) => r.requested && sameLogin(r.login, actor));
            if (!requested) remove(actor);
            add(author, { kind: 'newCommitsFrom', login: actor }, at);
          } else {
            // The author replying (comment or self-review) or pushing; a push
            // not known to be someone else's counts as the author's.
            const reason: Reason = e.type === 'push' ? { kind: 'newChanges' } : { kind: 'authorReplied' };
            for (const r of reviewers.values()) {
              if (isAuthor(r.login) || r.lastReview === 'APPROVED') continue;
              if (r.requested) add(r.login, { kind: 'reviewRequested' }, at);
              else if (r.commented) add(r.login, reason, at);
            }
            if (!unresolvedByOthers) removeCertainly(author);
          }
          break;
      }
      emptySince = members.length ? null : (emptySince ?? at);
    }

    // The current requested list is authoritative for "review requested".
    for (const login of input.requested)
      if (!isAuthor(login) && !isBot(login) && !find(login)) add(login, { kind: 'reviewRequested' }, null);
    let result = members.filter(
      (m) => m.reason.kind !== 'reviewRequested' || input.requested.some((r) => sameLogin(r, m.login)),
    );
    result = result.filter((m) => !isBot(m.login));
    members.length = 0;
    members.push(...result);
    if (!members.length && !isBot(author)) {
      const all = [...reviewers.values()];
      const approved = all.some((r) => r.lastReview === 'APPROVED');
      const reason: Reason =
        approved && !anyUnresolved
          ? { kind: 'readyToMerge' }
          : !all.some((r) => r.requested || r.commented || r.lastReview !== null)
            ? { kind: 'noReviewers' }
            : { kind: 'waiting' };
      // Under a cut-off timeline, the set may have emptied at another time.
      add(author, reason, truncated ? null : emptySince);
    }
  }

  /** Applies the overrides made against the latest timeline state, on
   *  drafts too. Bot events change nothing in the computed set, so they do
   *  not void an override either. */
  function applyOverrides() {
    let latest: string | null = null;
    for (const e of input.events) if (!isBot(e.actor) && (latest === null || compareTimes(e.at, latest) > 0)) latest = e.at;
    // A missing basis compares like an unparsable one: oldest.
    const basis = (o: AttentionOverride) => o.basis ?? '';
    const overrides = input.overrides
      .map((o, i) => [o, i] as const)
      .sort(([a, i], [b, j]) => compareTimes(basis(a), basis(b)) || i - j)
      .map(([o]) => o);
    for (const o of overrides) {
      if ((latest !== null && compareTimes(latest, basis(o)) > 0) || isBot(o.login)) continue;
      if (o.state === 'add') add(o.login, { kind: 'manual' }, o.setAt, true);
      else remove(o.login);
    }
  }
}

/** Thrown by the engine-only calls of the fallback. */
export class NeedsEngine extends Error {
  constructor(what: string) {
    super(`${what} needs the diff engine, which is not loaded`);
    this.name = 'NeedsEngine';
  }
}

/**
 * Carrying marks to a later blob needs the engine's line map: without it
 * marks on an earlier blob cover nothing (the review model treats a blob
 * with no line map as unrelated), so nothing shows reviewed that the
 * engine would not show.
 */
function lineMap(): number[] {
  throw new NeedsEngine('Carrying review marks to a later version');
}

/**
 * Key-based marks are the engine's keys: only the engine can say what they
 * cover. Resolution waits for a session with the engine; answering "covers
 * nothing" here would retire marks that still apply.
 */
function resolveLegacy(): LegacyResolution[] {
  throw new NeedsEngine('Resolving key-based review marks');
}

export const fallbackEngine: EngineApi = {
  languages: () => LANGS,
  detectLanguage,
  highlight,
  symbols,
  parsePatch,
  blocks,
  lineMap,
  resolveLegacy,
  detectMoves,
  attention,
};
